import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save, Beaker, Check, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchJSON } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function EditStock() {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();

    // URL Params
    const paramTankId = searchParams.get("tankId") || "";
    const paramDate = searchParams.get("date") || new Date().toISOString().split("T")[0];

    const [date, setDate] = useState<string>(paramDate);
    const [tankId, setTankId] = useState<string>(paramTankId);
    const [tankName, setTankName] = useState<string>("");
    const [productName, setProductName] = useState<string>("");

    // Form Fields
    const [openingStock, setOpeningStock] = useState<string>("0");
    const [receiptStock, setReceiptStock] = useState<string>("0");
    const [totalStock, setTotalStock] = useState<string>("0.00");
    const [meterSale, setMeterSale] = useState<string>("0");
    const [closingStock, setClosingStock] = useState<string>("0.00");
    const [closingDipReading, setClosingDipReading] = useState<string>(""); // Scale value / after_dip
    const [dipStock, setDipStock] = useState<string>(""); // Volume in liters / after_stock
    const [variation, setVariation] = useState<string>("NaN");
    const [variAmount, setVariAmount] = useState<string>("NaN");
    const [rateRevision, setRateRevision] = useState<string>("NaN");
    const [density, setDensity] = useState<string>("0.00");

    // Density Modal State
    const [densityModalOpen, setDensityModalOpen] = useState(false);
    const [tempReading, setTempReading] = useState<string>("0.00");
    const [hydroReading, setHydroReading] = useState<string>("0.00");
    const [measuredDensity, setMeasuredDensity] = useState<string>("0.00");

    const [loading, setLoading] = useState(false);

    // Fetch Tanks List to match Names
    const { data: tanks = [] } = useQuery({
        queryKey: ["/api/tanks-list"],
        queryFn: async () => {
            const response = await fetchJSON('/api/tanks-list');
            const result = await response.json();
            if (!result.ok) throw new Error(result.error || 'Failed to fetch tanks');
            return result.rows || [];
        },
        staleTime: 0,
        refetchOnMount: "always",
    });

    // Fetch existing data for this tank/date
    const { data: existingData } = useQuery({
        queryKey: ["/api/reports/opening-stock", date],
        queryFn: async () => {
            if (!date) return [];
            const res = await fetchJSON(`/api/reports/opening-stock?from=${date}&to=${date}`);
            const json = await res.json();
            if (!json.ok) return [];
            return json.rows || [];
        },
        enabled: !!date,
        staleTime: 0,
        refetchOnMount: "always",
    });

    // Set Tank and Product Info
    useEffect(() => {
        if (tankId && tanks.length > 0) {
            const tank = tanks.find((t: any) => t.id === tankId || t._id === tankId);
            if (tank) {
                setTankName(tank.tank_number || tank.tankNumber || `T${tank.id}`);
                setProductName(tank.product_name || tank.fuel_products?.product_name || "");
            }
        }
    }, [tankId, tanks]);

    // Pre-fill form if data exists for selected date and tank
    useEffect(() => {
        if (existingData && existingData.length > 0 && tankId) {
            const row = existingData.find((r: any) => String(r.tank_id) === String(tankId));
            if (row) {
                setOpeningStock(row.opening_stock?.toString() || "0");
                setReceiptStock(row.stock_received?.toString() || row.receipt_quantity?.toString() || "0");
                setMeterSale(row.meter_sale?.toString() || "0");
                setClosingDipReading(row.after_dip?.toString() || row.dip_details?.toString() || "");
                setDipStock(row.after_stock?.toString() || "");
                setRateRevision(row.rate_revision?.toString() || "NaN");
                setDensity(row.density?.toString() || "0.00");
                setTempReading(row.temp?.toString() || "0.00");
                setHydroReading(row.hydrometer?.toString() || row.hydro?.toString() || "0.00");
                setMeasuredDensity(row.density?.toString() || "0.00");
            } else {
                // Reset form values to defaults if no record exists for this tank
                setOpeningStock("0");
                setReceiptStock("0");
                setMeterSale("0");
                setClosingDipReading("");
                setDipStock("");
                setRateRevision("NaN");
                setDensity("0.00");
                setTempReading("0.00");
                setHydroReading("0.00");
                setMeasuredDensity("0.00");
            }
        }
    }, [existingData, tankId]);

    // Auto Calculations
    useEffect(() => {
        const opStockVal = parseFloat(openingStock) || 0;
        const rxStockVal = parseFloat(receiptStock) || 0;
        const totalStockVal = opStockVal + rxStockVal;
        setTotalStock(totalStockVal.toFixed(2));

        const meterSaleVal = parseFloat(meterSale) || 0;
        const clStockVal = totalStockVal - meterSaleVal;
        setClosingStock(clStockVal.toFixed(2));

        const dStockVal = parseFloat(dipStock);
        if (isNaN(dStockVal)) {
            setVariation("NaN");
            setVariAmount("NaN");
        } else {
            const variationVal = dStockVal - clStockVal;
            setVariation(variationVal.toFixed(2));

            const rateVal = parseFloat(rateRevision);
            if (!isNaN(rateVal)) {
                const amountVal = variationVal * rateVal;
                setVariAmount(amountVal.toFixed(2));
            } else {
                setVariAmount("NaN");
            }
        }
    }, [openingStock, receiptStock, meterSale, dipStock, rateRevision]);

    const handleSave = async () => {
        if (!tankId || !date) {
            toast({ variant: "destructive", title: "Missing Fields", description: "Date and Tank are required." });
            return;
        }

        try {
            setLoading(true);
            const payload = {
                date,
                tank_id: tankId,
                product_name: productName,
                before_stock: parseFloat(openingStock) || 0,
                quantity: parseFloat(receiptStock) || 0,
                meter_sale: parseFloat(meterSale) || 0,
                after_dip: closingDipReading ? parseFloat(closingDipReading) : null,
                after_stock: dipStock ? parseFloat(dipStock) : null,
                stock_difference: variation !== "NaN" ? parseFloat(variation) : null,
                variation_amount: variAmount !== "NaN" ? parseFloat(variAmount) : null,
                rate_revision: rateRevision !== "NaN" ? parseFloat(rateRevision) : null,
                temp: parseFloat(tempReading) || 0,
                hydro: parseFloat(hydroReading) || 0,
                density: parseFloat(density) || 0,
            };

            const res = await fetchJSON('/api/stock-dump', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            const result = await res.json();
            if (!result.ok) throw new Error(result.error);

            // Invalidate queries to refresh data
            await queryClient.invalidateQueries({ queryKey: ["/api/reports/opening-stock"] });
            await queryClient.invalidateQueries({ queryKey: ["/api/tanks-list"] });

            toast({ title: "Success", description: "Stock Record Updated Successfully" });
            navigate('/opening-stock');
        } catch (e: any) {
            toast({ variant: "destructive", title: "Error", description: e.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            {/* Breadcrumb & Navigation */}
            <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="cursor-pointer hover:underline" onClick={() => navigate('/dashboard')}>🏠 Dashboard</span>
                        <span>/</span>
                        <span className="cursor-pointer hover:underline" onClick={() => navigate('/opening-stock')}>Stock Report</span>
                        <span>/</span>
                        <span className="text-gray-900 font-semibold">Edit Stock</span>
                    </div>
                </div>
                <Button variant="ghost" onClick={() => navigate('/opening-stock')}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
            </div>

            {/* Blue Form Card */}
            <Card className="bg-[#4f46e5] text-white rounded-xl shadow-lg border-none overflow-hidden">
                <CardContent className="p-6 space-y-6">
                    <div className="border-b border-white/20 pb-4">
                        <h2 className="text-2xl font-bold">Edit Stock</h2>
                    </div>

                    {/* Date Picker Row */}
                    <div className="flex items-center max-w-md">
                        <Button className="bg-[#f59e0b] hover:bg-[#d97706] text-black font-semibold rounded-r-none h-10 px-6">
                            Choose Date
                        </Button>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="bg-white text-black rounded-l-none border-none h-10 focus:ring-0 focus-visible:ring-0 font-medium"
                        />
                    </div>

                    {/* Main Grid Row 1 */}
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Tank</Label>
                            <Input value={tankName} readOnly className="bg-white text-black font-semibold h-10 border-none" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Product</Label>
                            <Input value={productName} readOnly className="bg-white text-black font-semibold h-10 border-none" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Opening Stock</Label>
                            <Input
                                value={openingStock}
                                onChange={(e) => setOpeningStock(e.target.value)}
                                className="bg-white text-black font-bold h-10 border-none"
                            />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Receipt Stock</Label>
                            <Input
                                value={receiptStock}
                                onChange={(e) => setReceiptStock(e.target.value)}
                                className="bg-white text-black font-medium h-10 border-none"
                            />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Total Stock</Label>
                            <Input value={totalStock} readOnly className="bg-white text-black font-medium h-10 border-none" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold flex items-center gap-1">
                                Meter Sale <span className="text-orange-400">⛽</span>
                            </Label>
                            <Input
                                value={meterSale}
                                onChange={(e) => setMeterSale(e.target.value)}
                                className="bg-white text-black font-medium h-10 border-none"
                            />
                        </div>
                    </div>

                    {/* Main Grid Row 2 */}
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Closing Stock</Label>
                            <Input value={closingStock} readOnly className="bg-white text-black font-medium h-10 border-none" />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Closing DIP Reading</Label>
                            <div className="space-y-2">
                                <Input
                                    value={closingDipReading}
                                    placeholder="Scale value"
                                    onChange={(e) => setClosingDipReading(e.target.value)}
                                    className="bg-white text-black font-medium h-10 border-none"
                                />
                                <Input
                                    value={dipStock}
                                    placeholder="DIP stock"
                                    onChange={(e) => setDipStock(e.target.value)}
                                    className="bg-white text-black font-medium h-10 border-none"
                                />
                            </div>
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Variation</Label>
                            <Input
                                value={variation}
                                readOnly
                                className={`bg-white font-bold h-10 border-none ${
                                    variation !== "NaN" && parseFloat(variation) < 0 ? "text-red-600" : "text-green-600"
                                }`}
                            />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Vari. Amount (₹)</Label>
                            <Input
                                value={variAmount}
                                onChange={(e) => setVariAmount(e.target.value)}
                                className="bg-white text-black font-medium h-10 border-none"
                            />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Rate Revision (₹)</Label>
                            <Input
                                value={rateRevision}
                                onChange={(e) => setRateRevision(e.target.value)}
                                className="bg-white text-black font-medium h-10 border-none"
                            />
                        </div>
                        <div className="col-span-2 space-y-1">
                            <Label className="text-white text-xs font-semibold">Density</Label>
                            <div className="flex items-center gap-2">
                                <div
                                    onClick={() => setDensityModalOpen(true)}
                                    className="w-10 h-10 rounded bg-white text-indigo-700 flex items-center justify-center cursor-pointer hover:bg-indigo-50 border border-white/20 transition-colors shadow-sm"
                                >
                                    <span className="text-2xl">🧪</span>
                                </div>
                                <span className="font-bold text-lg bg-indigo-800/40 px-3 py-1.5 rounded-md min-w-[70px] text-center border border-indigo-400/20">
                                    {parseFloat(density).toFixed(3)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Action Button */}
                    <div className="flex justify-center pt-4">
                        <Button
                            onClick={handleSave}
                            disabled={loading}
                            className="bg-green-500 hover:bg-green-600 text-white min-w-[200px] h-11 rounded-lg gap-2 text-base font-semibold shadow-md"
                        >
                            <Save className="w-5 h-5" /> {loading ? "UPDATING..." : "UPDATE"}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Density Modal */}
            <Dialog open={densityModalOpen} onOpenChange={setDensityModalOpen}>
                <DialogContent className="sm:max-w-[600px] bg-white text-black p-0 overflow-hidden rounded-xl border-none">
                    <DialogHeader className="bg-blue-600 text-white px-6 py-4 flex items-center justify-between">
                        <DialogTitle className="text-lg font-bold">Density Test</DialogTitle>
                    </DialogHeader>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-gray-700">Temperature</Label>
                                <Input
                                    value={tempReading}
                                    onChange={(e) => setTempReading(e.target.value)}
                                    className="border-gray-300 h-10 font-semibold"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-gray-700">Hydro Meter Reading</Label>
                                <Input
                                    value={hydroReading}
                                    onChange={(e) => setHydroReading(e.target.value)}
                                    className="border-gray-300 h-10 font-semibold"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-semibold text-gray-700">Measured Density</Label>
                                <Input
                                    value={measuredDensity}
                                    onChange={(e) => setMeasuredDensity(e.target.value)}
                                    className="border-gray-300 h-10 font-semibold"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter className="flex justify-center gap-3 pb-6 px-6">
                        <Button
                            className="bg-green-600 hover:bg-green-700 text-white font-semibold w-24 h-10 gap-1.5"
                            onClick={() => {
                                setDensity(measuredDensity);
                                setDensityModalOpen(false);
                                toast({
                                    title: "Density Applied",
                                    description: `Set density to ${measuredDensity}`
                                });
                            }}
                        >
                            <Check className="w-4 h-4" /> Ok
                        </Button>
                        <Button
                            variant="outline"
                            className="border-red-500 text-red-500 hover:bg-red-50 font-semibold w-24 h-10 gap-1.5"
                            onClick={() => setDensityModalOpen(false)}
                        >
                            <X className="w-4 h-4" /> Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

